import { useEffect, useRef, memo, useState, CSSProperties, useCallback } from 'react';
import { LottieContainer } from '@/sub-luca-plugin/compatible-components';
import CollectAction, { ICollectActionProps } from '@/sub-luca-plugin/components/collect-action';
import { environment } from '@gaodun.com/gtools';
import { createPortal } from 'react-dom';
import { CloseOutlined } from '@ant-design/icons';
import { eventBusExample } from '@/sub-luca-plugin/utils/class-example';
import { INoticeData } from '@/sub-luca-plugin/types';
import { qiankunClassExample } from '@/sub-luca-plugin/utils/tools';
interface VideoIProps {
    videoId?: string;
    initialTime?: number;
    autoplay?: boolean;
    fragmentStartTime?: number;
    fragmentEndTime?: number;
    isKeyframe?: boolean;
    onMount?: () => void;
    onInitError?: () => void;
}

export const SubPlayer = (props: VideoIProps) => {
    const {
        videoId,
        autoplay = false,
        isKeyframe = false,
        initialTime = 0,
        fragmentStartTime,
        fragmentEndTime,
        onMount,
        onInitError,
    } = props;
    const microAppContainer = useRef<HTMLDivElement | null>(null);

    const initQiankun = () => {
        return qiankunClassExample.loadMicroApp(
            {
                name: 'study-player',
                entry: `https://${environment.getApiEnv()}sub-study-player.gaodun.com`,
                // entry: 'https://dev-sub-study-player.gaodun.com:3038',
                container: microAppContainer.current!,
                props: {
                    playerType: 'luca',
                    videoId,
                    initialTime: initialTime,
                    autoplay,
                    isKeyframe,
                    fragmentStartTime,
                    fragmentEndTime,
                    initPlayer: onMount,
                    onInitError,
                },
            },
            // 取消沙箱隔离
            {
                sandbox: false,
            },
            {
                afterMount: async () => {
                    (document.getElementById('luca-sub-player-container')?.firstChild as HTMLElement)?.setAttribute(
                        'style',
                        'width: 100%; height: 100%;'
                    );
                },
            }
        );
    };

    useEffect(() => {
        const microApp = initQiankun();
        return () => {
            microApp?.unmount();
        };
    }, []);

    return (
        <div
            id="luca-sub-player-container"
            style={{ width: '48vw', height: '27vw', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}
            ref={microAppContainer}
        ></div>
    );
};

const VideoPlayerModal = () => {
    const currentCollectStatus = useRef<boolean>();
    const videoInfo = useRef<Omit<VideoIProps, 'onMount' | 'onInitError'>>({});
    const callbacksRef = useRef<Record<string, (data: any) => void>>();
    const [visible, setVisible] = useState(false);
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [collectActionInfo, setCollectActionInfo] = useState<Omit<ICollectActionProps, 'type'>>({});

    const styles = useRef<Record<string, CSSProperties>>({
        modalSubPlayerOverlay: {
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            zIndex: 2000,
            top: 0,
            left: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        modalSubPlayer: {
            width: '48vw',
            height: '27vw',
            position: 'relative',
            background: 'rgba(0,0,0,1)',
            borderRadius: ' 0 0 12px 12px',
        },
        modalSubPlayerHeader: {
            width: '100%',
            height: '64px',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box',
            padding: '10px 16px 0 16px',
            position: 'absolute',
            zIndex: '10',
            left: 0,
            top: 0,
            transform: 'translateY(-100%)',
            background: '#fff',
            borderRadius: '12px 12px 0 0',
        },
        modalSubPlayerTitle: {
            flex: 1,
            fontSize: '20px',
            fontWeight: 500,
            color: 'rgba(27, 27, 72, 1)',
            wordBreak: 'break-word',
        },
        modalSubPlayerClose: {
            position: 'absolute',
            zIndex: '10',
            top: '-100px',
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: '#fff',
            fontSize: '16px',
            color: '#000',
            cursor: 'pointer',
        },
        modalSubPlayerLoading: {
            width: '90px',
            position: 'absolute',
            zIndex: 1,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            fontSize: '30px',
            borderRadius: '10px',
            overflow: 'hidden',
            backgroundColor: '#fff',
        },
        modalSubPlayerLoadingP: {
            fontSize: '16px',
            color: '#fff',
        },
        modalSubPlayerLoadingIcon: {
            width: '30px',
            height: '30px',
            display: 'inline-block',
            background: 'url(https://simg01.gaodunwangxiao.com/uploadfiles/files/202408/20/32392_20240820163404.gif) center/cover',
        },
        collected: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            fontWeight: 600,
            color: '#9655FF',
        },
        notCollected: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            fontWeight: 600,
            color: '#1B1B48',
        },
        notCollectePng: {
            width: '20px',
            height: '20px',
            background: "url('https://simg01.gaodunwangxiao.com/uploadfiles/files/202408/28/6eba8_20240828155655.png') center/19px 19px",
        },
        collectText: {
            marginLeft: '4px',
        },
        closeIcon: {
            width: '16px',
            height: '16px',
        },
    });

    const handleCollectActionChange = useCallback((status: boolean) => {
        currentCollectStatus.current = status;
    }, []);

    const handleLoaded = () => setLoading(false);

    const handleClose = () => {
        videoInfo.current = {};
        callbacksRef.current?.onClose({ collectFlag: currentCollectStatus.current });
        setVisible(false);
        setLoading(false);
        setCollectActionInfo({});
    };

    useEffect(() => {
        eventBusExample.subscribe('VideoPlayerModal', ({ type, params = {}, callbacks }: INoticeData) => {
            const {
                title,
                videoId,
                initialTime,
                autoplay,
                recordId,
                projectId,
                subjectId,
                twoLevelSubjectId,
                fragmentStartTime,
                fragmentEndTime,
                isKeyframe,
            } = params;
            switch (type) {
                case 'open':
                    {
                        videoInfo.current = { videoId, initialTime, autoplay, fragmentStartTime, fragmentEndTime, isKeyframe };
                        callbacksRef.current = callbacks;
                        setLoading(true);
                        setCollectActionInfo({ id: recordId, projectId, subjectId, twoLevelSubjectId });
                        setTitle(title);
                        setVisible(true);
                    }
                    break;
                default:
                    handleClose();
                    break;
            }
        });
    }, []);

    return visible
        ? createPortal(
              <div style={styles.current.modalSubPlayerOverlay}>
                  {loading && (
                      <div style={styles.current.modalSubPlayerLoading}>
                          <LottieContainer type="complexLoading" excuteType="autoPlayByInit" loop={true} />
                      </div>
                  )}
                  <div style={styles.current.modalSubPlayer}>
                      <div style={styles.current.modalSubPlayerClose} onClick={handleClose}>
                          <CloseOutlined style={styles.current.closeIcon} />
                      </div>
                      <div style={styles.current.modalSubPlayerHeader}>
                          <span style={styles.current.modalSubPlayerTitle}>{title}</span>
                          {collectActionInfo.id && (
                              <CollectAction {...collectActionInfo} type="resource" onChange={handleCollectActionChange} />
                          )}
                      </div>
                      <SubPlayer {...videoInfo.current} onMount={handleLoaded} onInitError={handleClose} />
                  </div>
              </div>,
              document.body
          )
        : null;
};

export default memo(VideoPlayerModal);
